import { zodResolver } from "@hookform/resolvers/zod";
import { SeatIcon, UsersIcon } from "@phosphor-icons/react";
import { useId } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import type z from "zod";

import { Button } from "@/components/Button/Button";
import { Card } from "@/components/Card/Card";
import { CounterButton } from "@/components/CounterButton/CounterButton";
import { Radio } from "@/components/Radio/Radio";
import { RadioGroup } from "@/components/Radio/RadioGroup";
import { TravellerTypeSelectionItem } from "@/components/TravellerTypeSelectionItem/TravellerTypeSelectionItem";
import {
  cabinClassLabels,
  cabinClassOptions,
} from "@/features/flights/utils/cabinClasses";
import { flightSearchFormInputSchema } from "@/features/flights/utils/flightsValidation";
import { MAX_TRAVELLERS } from "@/utils/constants";

const travellersFormSchema = flightSearchFormInputSchema.pick({
  adult: true,
  child: true,
  infantWithoutSeat: true,
  cabinClass: true,
});
type TravellersFormValues = z.infer<typeof travellersFormSchema>;

type TravellersSelectionProps = {
  initialValues: TravellersFormValues;
  onConfirm: (values: TravellersFormValues) => void;
};

export function TravellersSelection({
  initialValues,
  onConfirm,
}: TravellersSelectionProps) {
  const intl = useIntl();
  const cabinClassId = useId();

  const { control, handleSubmit } = useForm({
    defaultValues: initialValues,
    resolver: zodResolver(travellersFormSchema),
  });

  const [adult, child] = useWatch({ control, name: ["adult", "child"] });

  const maximumChild = MAX_TRAVELLERS - adult;
  const maximumAdult = MAX_TRAVELLERS - child;

  return (
    <form
      onSubmit={(event) => {
        event.stopPropagation();
        handleSubmit(onConfirm)(event);
      }}
      className="flex w-full basis-full flex-col gap-3"
    >
      <Card
        icon={
          <UsersIcon
            size={24}
            color="var(--color-surface-pressed-accent-strong)"
          />
        }
        title={intl.formatMessage({
          defaultMessage: "Passengers",
          description: "flights.search.travellers",
        })}
      >
        <div className="-my-3 divide-y divide-stroke-neutral-soft *:py-3">
          <TravellerTypeSelectionItem
            label={intl.formatMessage({
              defaultMessage: "Adult",
              description: "flights.search.adult",
            })}
            description={intl.formatMessage({
              defaultMessage: "12+ years old",
              description: "flights.search.twelveYearOldPlus",
            })}
          >
            <Controller
              name="adult"
              control={control}
              render={({ field }) => (
                <CounterButton
                  value={field.value}
                  onChange={field.onChange}
                  plusButtonDisabled={field.disabled}
                  minusButtonDisabled={field.disabled}
                  minValue={1}
                  maxValue={maximumAdult}
                />
              )}
            />
          </TravellerTypeSelectionItem>

          <TravellerTypeSelectionItem
            label={intl.formatMessage({
              defaultMessage: "Child",
              description: "flights.search.child",
            })}
            description={intl.formatMessage({
              defaultMessage: "2-12 years old",
              description: "flights.search.twoToTwelveYearOld",
            })}
          >
            <Controller
              name="child"
              control={control}
              render={({ field }) => (
                <CounterButton
                  value={field.value}
                  onChange={field.onChange}
                  plusButtonDisabled={field.disabled}
                  minusButtonDisabled={field.disabled}
                  maxValue={maximumChild}
                />
              )}
            />
          </TravellerTypeSelectionItem>

          <TravellerTypeSelectionItem
            label={intl.formatMessage({
              defaultMessage: "Infant",
              description: "flights.search.infant",
            })}
            description={intl.formatMessage({
              defaultMessage: "Less than 2 years old",
              description: "flights.search.underTwoYearOld",
            })}
          >
            <Controller
              name="infantWithoutSeat"
              control={control}
              render={({ field }) => (
                <CounterButton
                  value={field.value}
                  onChange={field.onChange}
                  plusButtonDisabled={field.disabled}
                  minusButtonDisabled={field.disabled}
                  maxValue={adult}
                />
              )}
            />
          </TravellerTypeSelectionItem>
        </div>
      </Card>

      <Card
        icon={
          <SeatIcon
            size={24}
            color="var(--color-surface-pressed-accent-strong)"
          />
        }
        title={
          <span id={cabinClassId}>
            {intl.formatMessage({
              defaultMessage: "Cabin class",
              description: "flights.cabinClass.cabinClass",
            })}
          </span>
        }
      >
        <div>
          <Controller
            control={control}
            name="cabinClass"
            render={({ field }) => (
              <RadioGroup
                aria-labelledby={cabinClassId}
                defaultValue="economy"
                className="-ms-2 space-y-3!"
                {...field}
              >
                {cabinClassOptions.map((value) => (
                  <Radio
                    key={value}
                    value={value}
                    label={intl.formatMessage(cabinClassLabels[value])}
                  />
                ))}
              </RadioGroup>
            )}
          />
        </div>
      </Card>

      <div className="mt-auto">
        <Button variant="contained" size="medium" type="submit" fullWidth>
          {intl.formatMessage({
            defaultMessage: "Confirm",
            description: "confirm",
          })}
        </Button>
      </div>
    </form>
  );
}
